/**
 * Layers a marker onto a quantity column: the rows `applies` names render through `Marker`, every
 * other row through whatever the column rendered before — the markers layered earlier, then its own
 * `cellRenderer`, then its formatted value. Built on AG Grid's `cellRendererSelector`, which the grid
 * re-runs whenever it redraws the cell.
 *
 * AG Grid redraws a cell on a row update only when its value or formatted value changes, and a
 * marker follows row state the value does not carry — an unpriced row's price is empty under every
 * market date. So a marked column redraws on every refresh of its row (`equals` never holds), and a
 * row moves in and out of its markers with nothing else to rely on.
 *
 * The marker layered last is asked first.
 */
import type { ColDef } from "ag-grid-community";
import type { CustomCellRendererProps } from "ag-grid-react";
import type { Instrument } from "../const";

export type CellMarker<P extends object = object> = {
	applies(row: Instrument): boolean;
	Marker: React.ComponentType<CustomCellRendererProps<Instrument> & P>;
} & (Partial<P> extends P
	? { /** Handed to `Marker` on top of the cell's own params. */ params?: P }
	: { /** Handed to `Marker` on top of the cell's own params. */ params: P });

function neverEqual(): boolean {
	return false;
}

export function withCellMarker<P extends object, T extends Instrument = Instrument>(
	column: ColDef<T>,
	{ applies, Marker, params }: CellMarker<P>,
): ColDef<T> {
	const previous = column.cellRendererSelector;
	return {
		...column,
		equals: neverEqual,
		cellRendererSelector: (cellParams) =>
			cellParams.data != null && applies(cellParams.data) ? { component: Marker, params } : previous?.(cellParams),
	};
}
